import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Chrome, 
  Puzzle, 
  Zap, 
  ArrowRight, 
  CheckCircle2, 
  Globe2, 
  Shield, 
  Sparkles, 
  Copy, 
  Save, 
  Image, 
  Video, 
  Code, 
  FileText, 
  History, 
  User, 
  Trash2, 
  LogOut, 
  Target,
  Rocket,
  Lock,
  Wand2,
  Crown
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { User as SupabaseUser } from '@supabase/supabase-js';
import { Template } from "@/types";
import { TemplatesTab } from "@/components/TemplatesTab";
import Footer from "@/components/Footer";
import Layout from "@/components/Layout";
import PromptHistoryModal from "@/components/PromptHistoryModal";
import AllHistoryModal from "@/components/AllHistoryModal";

interface Prompt {
  id: string;
  raw_prompt: string;
  refined_prompt: string;
  category: string;
  created_at: string;
  score?: number;
  feedback?: string[];
}

const Dashboard = () => {
  const [rawPrompt, setRawPrompt] = useState("");
  const [category, setCategory] = useState("");
  const [refinedPrompt, setRefinedPrompt] = useState("");
  const [promptScore, setPromptScore] = useState<number | null>(null);
  const [promptFeedback, setPromptFeedback] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [promptHistory, setPromptHistory] = useState<Prompt[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [activeTab, setActiveTab] = useState("refine");
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [isAllHistoryOpen, setIsAllHistoryOpen] = useState(false);
  const navigate = useNavigate();

  const { toast } = useToast();

  const categoryOptions = [
    { value: "image", label: "Image Generation", icon: Image, color: "from-pink-500 to-rose-500" },
    { value: "video", label: "Video Creation", icon: Video, color: "from-purple-500 to-indigo-500" },
    { value: "app", label: "App Development", icon: Code, color: "from-blue-500 to-cyan-500" },
    { value: "text", label: "Text Writing", icon: FileText, color: "from-green-500 to-emerald-500" },
    { value: "other", label: "Other", icon: FileText, color: "from-gray-500 to-slate-500" },
  ];

  useEffect(() => {
    // Check if user is authenticated
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }
      setUser(session.user);
    };

    checkAuth();
    loadPromptHistory();
  }, [navigate]);

  const loadPromptHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const response = await fetch(`https://fciwhyeenqiilggtnubv.supabase.co/functions/v1/prompt-history`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPromptHistory(data.prompts);
      }
    } catch (error) {
      console.error('Error loading history:', error);
      toast({
        title: "Error",
        description: "Failed to load prompt history.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rawPrompt.trim() || !category) {
      toast({
        title: "Missing information",
        description: "Please enter a prompt and select a category.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }

      const response = await fetch(`https://fciwhyeenqiilggtnubv.supabase.co/functions/v1/refine-prompt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          rawPrompt,
          category
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setRefinedPrompt(data.refinedPrompt);
        toast({
          title: "Prompt refined successfully!",
          description: "Your optimized prompt is ready to use.",
        });
        // Reload history to show the new prompt
        loadPromptHistory();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to refine prompt');
      }
    } catch (error) {
      console.error('Error refining prompt:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to refine prompt. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: "The refined prompt has been copied to your clipboard.",
    });
  };

  const deletePrompt = async (promptId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Error",
          description: "You must be logged in to delete prompts.",
          variant: "destructive",
        });
        return;
      }

      // Show loading toast
      const loadingToast = toast({
        title: "Deleting...",
        description: "Please wait while we delete your prompt.",
      });

      const response = await fetch(`https://fciwhyeenqiilggtnubv.supabase.co/functions/v1/delete-prompt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ promptId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete prompt');
      }

      // Update local state to remove the deleted prompt
      setPromptHistory(prev => prev.filter(p => p.id !== promptId));

        toast({
        title: "Success",
          description: "Prompt deleted successfully.",
        });
    } catch (error) {
      console.error('Error deleting prompt:', error);
      toast({
        title: "Error",
        description: error instanceof Error 
          ? error.message 
          : "Failed to delete prompt. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const getCategoryIcon = (cat: string) => {
    const option = categoryOptions.find(opt => opt.value === cat);
    if (!option) return FileText;
    return option.icon;
  };

  const handleScorePrompt = async () => {
    if (!rawPrompt.trim()) {
      toast({
        title: "Missing information",
        description: "Please enter a prompt to score.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login');
        return;
      }

      const response = await fetch(`https://fciwhyeenqiilggtnubv.supabase.co/functions/v1/score-prompt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: rawPrompt
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setPromptScore(data.score);
        setPromptFeedback(data.feedback);
        toast({
          title: "Prompt scored successfully!",
          description: "Check out your prompt's quality score and feedback.",
        });
        // Reload history to show the new prompt with score
        loadPromptHistory();
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to score prompt');
      }
    } catch (error) {
      console.error('Error scoring prompt:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to score prompt. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Add template handling
  const handleTemplateSelect = (template: Template) => {
    setRawPrompt(template.template_text);
    setCategory(template.category);
    setActiveTab("refine");
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
          {/* Header */}
        <header className="bg-background/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
            <div className="container mx-auto px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-primary-foreground" />
                  </div>
                <span className="text-xl font-bold text-foreground">
                    PromptPerfect
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                <span className="text-sm text-muted-foreground">
                    {user?.email}
                  </span>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => navigate('/profile')} 
                  className="text-muted-foreground hover:text-foreground"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleLogout} 
                  className="text-muted-foreground hover:text-foreground"
                >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </div>
              </div>
            </div>
          </header>

          <div className="container mx-auto px-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="py-4">
                <TabsList className="grid w-full max-w-[800px] grid-cols-4 mx-auto">
                  <TabsTrigger value="refine" className="flex items-center">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Refine Prompt
                  </TabsTrigger>
                  <TabsTrigger value="score" className="flex items-center">
                    <Target className="w-4 h-4 mr-2" />
                    Score & Feedback
                  </TabsTrigger>
                  <TabsTrigger value="templates" className="flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Templates
                  </TabsTrigger>
                  <TabsTrigger value="extension" className="flex items-center">
                    <Puzzle className="w-4 h-4 mr-2" />
                    Extension
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="py-8">
                <TabsContent value="refine">
                  <div className="grid lg:grid-cols-3 gap-8">
                    {/* Main Input Section */}
                    <div className="lg:col-span-2 space-y-6">
                    <Card className="border-border bg-card">
                        <CardHeader>
                        <CardTitle className="text-2xl font-bold text-foreground flex items-center">
                          <Sparkles className="w-6 h-6 mr-2 text-primary" />
                            Transform Your Idea
                          </CardTitle>
                        <CardDescription className="text-muted-foreground">
                            Enter your raw prompt or idea and select the category for AI optimization
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Your Raw Prompt or Idea
                              </label>
                              <Textarea
                                placeholder="Describe what you want to create... (e.g., 'make a logo for my coffee shop' or 'video about cooking pasta')"
                                value={rawPrompt}
                                onChange={(e) => setRawPrompt(e.target.value)}
                              className="min-h-[120px] bg-background text-foreground placeholder:text-muted-foreground"
                                maxLength={500}
                              />
                            <div className="mt-1 text-xs text-muted-foreground">
                                {rawPrompt.length}/500 characters
                              </div>
                            </div>
                            
                            <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Category
                              </label>
                              <Select value={category} onValueChange={setCategory}>
                              <SelectTrigger className="h-12 bg-background text-foreground">
                                  <SelectValue placeholder="Select the type of prompt you want to create" />
                                </SelectTrigger>
                                <SelectContent>
                                  {categoryOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      <div className="flex items-center">
                                        <option.icon className="w-4 h-4 mr-2" />
                                        {option.label}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            <Button
                              type="submit"
                            className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                              disabled={isProcessing}
                            >
                              {isProcessing ? (
                                <>
                                  <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                                  Refining Your Prompt...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4 mr-2" />
                                  Perfect My Prompt
                                </>
                              )}
                            </Button>
                          </form>
                        </CardContent>
                      </Card>

                      {refinedPrompt && (
                      <Card className="border-border bg-card">
                          <CardHeader>
                          <CardTitle className="text-xl font-bold text-foreground flex items-center">
                              ✨ Your Refined Prompt
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                          <div className="bg-background rounded-lg p-4 border border-border">
                            <p className="text-foreground">{refinedPrompt}</p>
                            </div>
                            <div className="flex gap-2 mt-4">
                              <Button
                                onClick={() => copyToClipboard(refinedPrompt)}
                              variant="outline"
                              className="flex-1 hover:bg-primary/10"
                              >
                                <Copy className="w-4 h-4 mr-2" />
                                Copy Prompt
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>

                  {/* Sidebar */}
                    <div className="space-y-6">
                      {/* Quick Stats */}
                    <Card className="border-border bg-card">
                        <CardHeader className="pb-3">
                        <CardTitle className="text-lg font-semibold text-foreground">Quick Stats</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">Total Prompts</span>
                          <Badge variant="secondary" className="bg-primary/10 text-primary">{promptHistory.length}</Badge>
                          </div>
                          {promptHistory.length > 0 && (
                            <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">Average Score</span>
                            <Badge variant="secondary" className="bg-primary/10 text-primary">
                                {Math.round(promptHistory.reduce((acc, prompt) => acc + (prompt.score || 0), 0) / promptHistory.length)}
                              </Badge>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Recent History */}
                    <Card className="border-border bg-card">
                        <CardHeader className="pb-3">
                        <CardTitle className="text-lg font-semibold text-foreground flex items-center justify-between">
                          <div className="flex items-center">
                            <History className="w-5 h-5 mr-2" />
                            Recent History
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsAllHistoryOpen(true)}
                            className="text-xs hover:bg-primary/10"
                          >
                            View All
                            <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {isLoadingHistory ? (
                          <div className="text-center text-muted-foreground">Loading...</div>
                          ) : promptHistory.length === 0 ? (
                          <div className="text-center text-muted-foreground">No prompts yet</div>
                          ) : (
                            promptHistory.slice(0, 5).map((item) => {
                              const IconComponent = getCategoryIcon(item.category);
                              return (
                              <div 
                                key={item.id} 
                                className="p-3 bg-background rounded-lg border border-border hover:border-primary transition-colors cursor-pointer"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedPrompt(item);
                                }}
                              >
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                    <IconComponent className="w-4 h-4 text-primary" />
                                    <Badge variant="outline" className="text-xs text-foreground">
                                        {categoryOptions.find(cat => cat.value === item.category)?.label}
                                      </Badge>
                                      {item.score && (
                                        <Badge variant="secondary" className={cn(
                                        "text-xs bg-primary/10",
                                        item.score >= 80 ? "text-green-500" :
                                        item.score >= 50 ? "text-yellow-500" :
                                        "text-red-500"
                                        )}>
                                          {item.score}/100
                                        </Badge>
                                      )}
                                    </div>
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        copyToClipboard(item.refined_prompt);
                                      }}
                                      className="h-6 w-6 p-0 hover:text-primary"
                                    >
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deletePrompt(item.id);
                                      }}
                                      className="h-6 w-6 p-0 text-destructive hover:text-destructive/90"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                                <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                                  {item.refined_prompt}
                                </p>
                                <div className="text-xs text-muted-foreground">
                                    {new Date(item.created_at).toLocaleDateString()}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="score">
                  <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-3 space-y-6">
                    <Card className="border-border bg-card">
                        <CardHeader>
                        <CardTitle className="text-2xl font-bold text-foreground flex items-center">
                          <Target className="w-6 h-6 mr-2 text-primary" />
                            Evaluate Your Prompt
                          </CardTitle>
                        <CardDescription className="text-muted-foreground">
                            Enter your prompt to get a quality score and personalized feedback
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-6">
                            <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Enter a Prompt to Score
                              </label>
                              <Textarea
                                placeholder="Enter a prompt to evaluate its quality and get personalized feedback..."
                                value={rawPrompt}
                                onChange={(e) => setRawPrompt(e.target.value)}
                              className="min-h-[120px] bg-background text-foreground placeholder:text-muted-foreground"
                                maxLength={500}
                              />
                            <div className="mt-1 text-xs text-muted-foreground">
                                {rawPrompt.length}/500 characters
                              </div>
                            </div>

                            <Button
                              onClick={handleScorePrompt}
                            className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                              disabled={isProcessing}
                            >
                              {isProcessing ? (
                                <>
                                  <Target className="w-4 h-4 mr-2 animate-spin" />
                                  Analyzing Prompt...
                                </>
                              ) : (
                                <>
                                  <Target className="w-4 h-4 mr-2" />
                                  Score My Prompt
                                </>
                              )}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>

                      {promptScore !== null && (
                      <Card className="border-border bg-card">
                          <CardHeader>
                          <CardTitle className="text-xl font-bold text-foreground flex items-center">
                            <Target className="w-6 h-6 mr-2 text-primary" />
                              Prompt Quality Score
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-6">
                            <div className="flex items-center justify-center">
                              <div className={cn(
                                "text-4xl font-bold rounded-full w-24 h-24 flex items-center justify-center",
                              promptScore >= 80 ? "bg-green-500/10 text-green-500" :
                              promptScore >= 50 ? "bg-yellow-500/10 text-yellow-500" :
                              "bg-red-500/10 text-red-500"
                              )}>
                                {promptScore}/100
                              </div>
                            </div>

                            <div className="space-y-4">
                            <h3 className="font-semibold text-foreground">Feedback & Tips</h3>
                              <div className="space-y-2">
                                {promptFeedback.map((feedback, index) => (
                                <div key={index} className="flex items-start gap-2 bg-background p-3 rounded-lg border border-border">
                                  <Zap className="w-5 h-5 text-primary mt-0.5" />
                                  <p className="text-foreground">{feedback}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="templates">
                  <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-3 space-y-6">
                      <TemplatesTab onSelectTemplate={handleTemplateSelect} />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="extension">
                  <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-3 space-y-8">
                      {/* Hero Section */}
                    <Card className="border-border bg-gradient-to-r from-primary to-primary-foreground text-white">
                        <CardContent className="p-8">
                          <div className="flex flex-col lg:flex-row items-center gap-8">
                            <div className="flex-1 space-y-4">
                              <h2 className="text-3xl font-bold">PromptPerfect Chrome Extension</h2>
                              <p className="text-lg opacity-90">
                                Enhance your AI interactions everywhere with our powerful Chrome extension.
                                Perfect your prompts directly in ChatGPT, Gemini, Notion, and more!
                              </p>
                              <Button 
                                size="lg"
                              className="bg-white text-primary hover:bg-white/90"
                                onClick={() => window.open('https://chrome.google.com/webstore', '_blank')}
                              >
                                <Chrome className="w-5 h-5 mr-2" />
                                Install Extension
                              </Button>
                            </div>
                            <div className="w-64 h-64 relative">
                              <div className="absolute inset-0 bg-white/10 backdrop-blur-lg rounded-xl shadow-2xl">
                                <div className="p-4">
                                  <div className="flex items-center gap-2 mb-4">
                                    <Chrome className="w-5 h-5" />
                                    <span className="font-medium">PromptPerfect</span>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="h-3 w-3/4 bg-white/20 rounded animate-pulse"></div>
                                    <div className="h-3 w-1/2 bg-white/20 rounded animate-pulse"></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Features Grid */}
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Supported Platforms */}
                      <Card className="border-border bg-card hover:shadow-xl transition-shadow">
                          <CardHeader>
                          <Globe2 className="w-8 h-8 text-primary" />
                            <CardTitle className="text-xl">Supported Platforms</CardTitle>
                          <CardDescription className="text-muted-foreground">Works seamlessly across popular AI platforms</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <ul className="space-y-3">
                              {[
                                "ChatGPT (chat.openai.com)",
                                "Gemini (gemini.google.com)",
                                "Notion",
                                "Any site with prompt inputs"
                              ].map((platform) => (
                              <li key={platform} className="flex items-center gap-2 text-foreground">
                                <CheckCircle2 className="w-4 h-4 text-primary" />
                                  <span>{platform}</span>
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>

                        {/* Smart Features */}
                      <Card className="border-border bg-card hover:shadow-xl transition-shadow">
                          <CardHeader>
                          <Wand2 className="w-8 h-8 text-primary" />
                            <CardTitle className="text-xl">Smart Features</CardTitle>
                          <CardDescription className="text-muted-foreground">Powerful tools at your fingertips</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <ul className="space-y-3">
                            <li className="flex items-start gap-2 text-foreground">
                              <Zap className="w-4 h-4 text-primary mt-1" />
                                <span>Instant prompt refinement with one click</span>
                              </li>
                            <li className="flex items-start gap-2 text-foreground">
                              <Target className="w-4 h-4 text-primary mt-1" />
                                <span>Real-time quality scoring and feedback</span>
                              </li>
                            <li className="flex items-start gap-2 text-foreground">
                              <ArrowRight className="w-4 h-4 text-primary mt-1" />
                                <span>One-click replace or copy refined prompts</span>
                              </li>
                            <li className="flex items-start gap-2 text-foreground">
                              <Save className="w-4 h-4 text-primary mt-1" />
                                <span>Save and access your prompt history</span>
                              </li>
                            </ul>
                          </CardContent>
                        </Card>

                        {/* Premium Benefits */}
                      <Card className="border-border bg-card hover:shadow-xl transition-shadow">
                          <CardHeader>
                          <Crown className="w-8 h-8 text-primary" />
                            <CardTitle className="text-xl">Premium Benefits</CardTitle>
                          <CardDescription className="text-muted-foreground">Unlock advanced capabilities</CardDescription>
                          </CardHeader>
                          <CardContent>
                            <ul className="space-y-3">
                            <li className="flex items-start gap-2 text-foreground">
                              <Rocket className="w-4 h-4 text-primary mt-1" />
                                <span>Auto-refine on submit</span>
                              </li>
                            <li className="flex items-start gap-2 text-foreground">
                              <Sparkles className="w-4 h-4 text-primary mt-1" />
                                <span>Custom tone and style settings</span>
                              </li>
                            <li className="flex items-start gap-2 text-foreground">
                              <Shield className="w-4 h-4 text-primary mt-1" />
                                <span>Priority API access</span>
                              </li>
                            <li className="flex items-start gap-2 text-foreground">
                              <Lock className="w-4 h-4 text-primary mt-1" />
                                <span>Access to premium templates</span>
                              </li>
                            </ul>
                          </CardContent>
                        </Card>
                      </div>

                      {/* How It Works */}
                    <Card className="border-border bg-card">
                        <CardHeader>
                        <CardTitle className="text-2xl font-bold text-foreground">How It Works</CardTitle>
                        <CardDescription className="text-muted-foreground">Simple steps to perfect your prompts everywhere</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid md:grid-cols-3 gap-6">
                            <div className="space-y-4">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                              <Puzzle className="w-6 h-6 text-primary" />
                              </div>
                            <h3 className="text-lg font-semibold text-foreground">1. Install & Login</h3>
                            <p className="text-muted-foreground">
                                Install the extension from Chrome Web Store and log in with your PromptPerfect account.
                              </p>
                            </div>
                            <div className="space-y-4">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                              <Wand2 className="w-6 h-6 text-primary" />
                              </div>
                            <h3 className="text-lg font-semibold text-foreground">2. Write Your Prompt</h3>
                            <p className="text-muted-foreground">
                                Start writing in any supported platform. You'll see the PromptPerfect button appear.
                              </p>
                            </div>
                            <div className="space-y-4">
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                              <Sparkles className="w-6 h-6 text-primary" />
                              </div>
                            <h3 className="text-lg font-semibold text-foreground">3. Perfect & Use</h3>
                            <p className="text-muted-foreground">
                                Click to refine, then use the improved prompt with one click. It's that simple!
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Installation Steps */}
                    <Card className="border-border bg-card">
                        <CardHeader>
                        <CardTitle className="text-2xl font-bold text-foreground">Quick Installation Guide</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ol className="space-y-6">
                            <li className="flex items-start gap-4">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                                1
                              </div>
                              <div className="flex-1">
                              <h4 className="font-semibold mb-2 text-foreground">Visit Chrome Web Store</h4>
                                <Button 
                                  variant="outline" 
                                className="mb-2 hover:bg-primary/10"
                                  onClick={() => window.open('https://chrome.google.com/webstore', '_blank')}
                                >
                                  <Chrome className="w-4 h-4 mr-2" />
                                  Open Chrome Web Store
                                </Button>
                              </div>
                            </li>
                            <li className="flex items-start gap-4">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                                2
                              </div>
                              <div className="flex-1">
                              <h4 className="font-semibold mb-2 text-foreground">Click "Add to Chrome"</h4>
                              <p className="text-muted-foreground">Follow the prompts to install the extension</p>
                              </div>
                            </li>
                            <li className="flex items-start gap-4">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                                3
                              </div>
                              <div className="flex-1">
                              <h4 className="font-semibold mb-2 text-foreground">Log in to Your Account</h4>
                              <p className="text-muted-foreground">Use your PromptPerfect credentials in the extension</p>
                              </div>
                            </li>
                          </ol>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>

              <TabsContent value="history" className="space-y-8">
                <div className="grid gap-6">
                  {isLoadingHistory ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
                  ) : promptHistory.length > 0 ? (
                    promptHistory.map((prompt) => {
                      const CategoryIcon = getCategoryIcon(prompt.category);
                      return (
                        <Card
                          key={prompt.id}
                          className="hover:shadow-md transition-shadow cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedPrompt(prompt);
                          }}
                        >
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-4 mb-4">
                                  <div className={cn(
                                    "p-2 rounded-lg",
                                    prompt.category === "image" && "bg-pink-100 text-pink-700 dark:bg-pink-900/20 dark:text-pink-400",
                                    prompt.category === "video" && "bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400",
                                    prompt.category === "app" && "bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
                                    prompt.category === "text" && "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400",
                                    prompt.category === "other" && "bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400",
                                  )}>
                                    <CategoryIcon className="w-5 h-5" />
          </div>
                                  <div className="flex items-center space-x-2">
                                    <Badge variant="outline">
                                      {categoryOptions.find(cat => cat.value === prompt.category)?.label}
                                    </Badge>
                                    {prompt.score && (
                                      <Badge variant="secondary" className={cn(
                                        "bg-primary/10",
                                        prompt.score >= 80 ? "text-green-500" :
                                        prompt.score >= 50 ? "text-yellow-500" :
                                        "text-red-500"
                                      )}>
                                        Score: {prompt.score}
                                      </Badge>
                                    )}
        </div>
      </div>
                                <div className="space-y-2">
                                  <p className="text-sm font-medium text-foreground line-clamp-2">
                                    {prompt.refined_prompt}
                                  </p>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs text-muted-foreground">
                                      {new Date(prompt.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 hover:text-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyToClipboard(prompt.refined_prompt);
                                  }}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive/90"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deletePrompt(prompt.id);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  ) : (
                    <div className="text-center py-8">
                      <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">No prompt history</h3>
                      <p className="text-sm text-muted-foreground">
                        Your refined prompts will appear here
                      </p>
                    </div>
                  )}
                </div>
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      <PromptHistoryModal
        prompt={selectedPrompt}
        isOpen={selectedPrompt !== null}
        onClose={() => {
          setSelectedPrompt(null);
        }}
        onCopy={(text) => {
          copyToClipboard(text);
          toast({
            title: "Copied to clipboard",
            description: "The prompt has been copied to your clipboard.",
          });
        }}
      />

      <AllHistoryModal
        prompts={promptHistory}
        isOpen={isAllHistoryOpen}
        onClose={() => setIsAllHistoryOpen(false)}
        onDelete={deletePrompt}
        onCopy={copyToClipboard}
        onViewDetails={setSelectedPrompt}
        categoryOptions={categoryOptions}
      />
    </Layout>
  );
};

export default Dashboard;
